import { connectColors, themeColors } from '@/theme/colors';
import { Icon } from '@lego/connect-components-react';
import { Flex, Input, Text } from '@lego/klik-ui';
import { useState } from 'react';
import type { SimulationIndexRowForDisplay } from '../../utils/simulation-index-types';

type IndexSectionCellProps = {
  row: SimulationIndexRowForDisplay;
  columnKey: keyof SimulationIndexRowForDisplay;
  formattedInitialData: SimulationIndexRowForDisplay[];
  previewBaseData?: SimulationIndexRowForDisplay[];
  isEditable: boolean;
  handleCellEdit: (
    cellValue: string,
    columnKey: keyof SimulationIndexRowForDisplay,
    priceListId: number,
  ) => void;
};

const IndexSectionCell = ({
  row,
  columnKey,
  formattedInitialData,
  previewBaseData,
  isEditable,
  handleCellEdit,
}: IndexSectionCellProps) => {
  const value =
    columnKey === 'priceList'
      ? `${row.priceList.id} / ${row.currency}`
      : (row[columnKey] as string);

  const isCellChanged = (
    row: SimulationIndexRowForDisplay,
    field: keyof SimulationIndexRowForDisplay,
    formattedInitialData: SimulationIndexRowForDisplay[],
  ) => {
    const originalRow = formattedInitialData.find(
      (r) => r.priceList.id === row.priceList.id,
    );
    return originalRow ? originalRow[field] !== row[field] : false;
  };

  // Orange: session edit vs initial data (normal editing mode only).
  // Pink: diff vs previous version (version preview mode only).
  const isChanged =
    previewBaseData === undefined &&
    isCellChanged(row, columnKey, formattedInitialData);
  const isChangedInPreview =
    previewBaseData !== undefined &&
    isCellChanged(row, columnKey, previewBaseData);

  const [showEditMode, setShowEditMode] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  return (
    <div
      key={`${row.priceList.id}-${columnKey}`}
      style={{
        padding: '4px',
        textAlign: columnKey === 'priceList' ? 'left' : 'right',
        backgroundColor: isChangedInPreview
          ? themeColors.highlight.pink.inverse
          : isChanged
            ? themeColors.support.warning.inverse
            : themeColors.surface.background.base.muted,
        borderRadius: '4px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      }}
    >
      {showEditMode ? (
        <Input
          height={'20px'}
          fontSize={'xs'}
          onBlur={() => {
            setShowEditMode(false);
            handleCellEdit(String(tempValue), columnKey, row.priceList.id);
          }}
          value={String(tempValue)}
          autoFocus
          onChange={(e) => setTempValue(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setShowEditMode(false);
              handleCellEdit(String(tempValue), columnKey, row.priceList.id);
            }
          }}
        />
      ) : columnKey === 'priceList' ? (
        <Flex justify={'space-between'} align={'center'}>
          <Flex gap={'4px'} align={'center'}>
            <Text className="label-xs-regular" isTruncated>
              {row.priceList.id}
            </Text>
            <Text className="label-sm-medium" isTruncated>
              {row.priceList.name}
            </Text>
          </Flex>
          <Text className="label-xs-regular" isTruncated>
            {row.currency}
          </Text>
        </Flex>
      ) : (
        <Flex align={'center'} justify={'center'} gap="4px">
          <Text
            className="label-sm-medium"
            color={
              isChangedInPreview
                ? themeColors.highlight.pink.default
                : isChanged
                  ? themeColors.support.warning.default
                  : connectColors.core.black
            }
            isTruncated
          >
            {String(value)}
          </Text>
          {isEditable && (
            <Icon
              aria-label="edit"
              name="pen"
              size="x-small"
              color={'ds-color-content-primary'}
              onClick={() => {
                setTempValue(value);
                setShowEditMode(true);
              }}
              style={{ cursor: 'pointer' }}
            />
          )}
        </Flex>
      )}
    </div>
  );
};
export default IndexSectionCell;
